import React, { useEffect, useState } from 'react';
import { Box } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import { AssignIdentityDrawer } from '../AssignIdentityDrawer/AssignIdentityDrawer';
import {
  IdentityAssignment,
  IdentityDisplayStatus,
} from '../IdentitiesGrid/types';
import { IdentityStatus } from '../IdentitiesGrid/IdentityStatus';
import { BackToIdentities } from './BackToIdentities';
import { IdentityAccessActions } from './IdentityAccessActions';
import { IdentityInfoPanel } from './IdentityInfoPanel';
import { IdentityRolesCard } from './IdentityRolesCard';
import { InfoColumn, Layout, RolesColumn } from './styles';
import { IdentityDetailsProps } from './types';
import { useRemoveMembershipRole } from './useRemoveMembershipRole';
import { useUpdateIdentityAccess } from './useUpdateIdentityAccess';

export function IdentityDetails({ identity }: IdentityDetailsProps) {
  const { enqueueSnackbar } = useSnackbar();
  const updateAccess = useUpdateIdentityAccess(identity.id);
  const removeRole = useRemoveMembershipRole(identity.id);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [assignments, setAssignments] = useState<IdentityAssignment[]>(
    identity.assignments,
  );
  const [status, setStatus] = useState(identity.status);

  useEffect(() => {
    setAssignments(identity.assignments);
    setStatus(identity.status);
  }, [identity.assignments, identity.status]);

  async function handleRemoveRole(assignmentId: string) {
    try {
      await removeRole.mutateAsync(assignmentId);
      enqueueSnackbar('Role removed', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Failed to remove role'), {
        variant: 'error',
      });
    }
  }

  async function handleStatusChange(nextStatus: IdentityDisplayStatus) {
    try {
      await updateAccess.mutateAsync({
        currentStatus: status,
        nextStatus,
      });
      enqueueSnackbar('Identity access updated', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(
        getApiErrorMessage(error, 'Failed to update identity access'),
        { variant: 'error' },
      );
    }
  }

  return (
    <>
      <BackToIdentities />
      <Layout>
        <InfoColumn>
          <IdentityInfoPanel identity={identity} />
          <Box mt={2.5}>
            <IdentityStatus status={status} />
          </Box>
          <IdentityAccessActions
            status={status}
            onStatusChange={handleStatusChange}
          />
        </InfoColumn>
        <RolesColumn>
          <IdentityRolesCard
            assignments={assignments}
            onAddRole={() => setIsAssignOpen(true)}
            onRemoveRole={handleRemoveRole}
          />
        </RolesColumn>
      </Layout>
      <AssignIdentityDrawer
        identity={identity}
        isOpen={isAssignOpen}
        handleDrawerClose={() => setIsAssignOpen(false)}
      />
    </>
  );
}
